'use client';

import React, { useState } from 'react';

import { LikeFilledNew } from '@depop/web-thrift-ui/Icons/LikeFilledNew';
import { LikeOutlineNew } from '@depop/web-thrift-ui/Icons/LikeOutlineNew';
import { Body } from '@depop/web-thrift-ui/Typography';
import { Modal } from '@depop/web-ui-kit/Modal';
import { clsx } from 'clsx';

import styles from './styles.module.css';

import { GuestLikesModal } from '@/app/[locale]/(home)/components/GuestLikes/GuestLikesModal';
import { ButtonMinimal } from '@/components/ButtonMinimal';
import { DownloadAppModal } from '@/components/DownloadAppModal/DownloadAppModal';
import { useGuestLikes } from '@/modules/guestLikes/hooks/useGuestLikes';
import { useIsGuestLikesEnabled } from '@/modules/guestLikes/hooks/useIsGuestLikesEnabled';
import { type GuestLikeProductInput } from '@/modules/guestLikes/types';
import { sendBoostedAttributionClick } from '@/modules/impressions/api';
import {
  UPDATE_LIKE_TYPE,
  useUpdateProductLike,
} from '@/modules/likes/hooks/useUpdateProductLike';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export type Props = {
  productId: number;
  isLiked?: boolean;
  likeCount?: number;
  isBoosted?: boolean;
  guestLikeProduct?: GuestLikeProductInput;
};

function LikeCount({ count }: { count: number }) {
  const displayCount = count > 99 ? '99+' : count;

  return (
    <Body
      className={styles.likeCount}
      variant="sm-bold"
      aria-label={`${displayCount} likes`}
    >
      {displayCount}
    </Body>
  );
}

function GuestLikesProductCardLike({
  product,
  likeCount,
}: {
  product: GuestLikeProductInput;
  likeCount?: number;
}) {
  const { isLiked, toggleGuestLike, isModalOpen, setIsModalOpen } =
    useGuestLikes(product.id);

  const IconComponent = isLiked ? LikeFilledNew : LikeOutlineNew;
  const count = (likeCount ?? 0) + (isLiked ? 1 : 0);

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    toggleGuestLike(product);
  }

  return (
    <div className={styles.container}>
      <ButtonMinimal
        onClick={handleClick}
        title={(isLiked ? 'Unlike' : 'Like') + ' Product'}
        aria-haspopup="dialog"
        {...(isModalOpen && { 'aria-controls': 'guest-likes-modal' })}
        className={clsx(styles.likeButton, !isLiked && styles.notLiked)}
      >
        <IconComponent />
      </ButtonMinimal>
      {likeCount !== undefined && count > 0 && <LikeCount count={count} />}
      <GuestLikesModal isOpen={isModalOpen} setOpen={setIsModalOpen} />
    </div>
  );
}

function DefaultProductCardLike({
  productId,
  isLiked: initialLikedState = false,
  likeCount: initialLikeCount,
  isBoosted,
}: Omit<Props, 'guestLikeProduct'>) {
  const [isAuthenticated, user] = useCurrentUser();
  const [isLiked, setIsLiked] = useState(initialLikedState);
  const [likeCount, setLikeCount] = useState(initialLikeCount ?? 0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const updateLikeMutation = useUpdateProductLike({ userId: user?.id });

  const IconComponent = isLiked ? LikeFilledNew : LikeOutlineNew;
  const hasLikes = initialLikeCount !== undefined && likeCount > 0;

  function sendProductInteractionAction(interactionType: string) {
    sendProductInteractionActionATEvent(productId, interactionType, {
      isBoosted,
    });
  }

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      setIsModalOpen(true);
      sendProductInteractionAction('ProductLike');
      return;
    }

    const newLikeState = !isLiked;

    setIsLiked(newLikeState);
    setLikeCount((prev) =>
      prev !== undefined ? prev + (newLikeState ? 1 : -1) : prev
    );

    if (newLikeState) {
      updateLikeMutation.mutate(
        { productId, type: UPDATE_LIKE_TYPE.ADD },
        {
          onSettled: () => {
            sendProductInteractionAction('ProductLike');
            if (isBoosted) {
              sendBoostedAttributionClick(productId);
            }
          },
          onError: () => {
            setIsLiked(false);
            setLikeCount((prev) => (prev !== undefined ? prev - 1 : prev));
          },
        }
      );
    } else {
      updateLikeMutation.mutate(
        { productId, type: UPDATE_LIKE_TYPE.REMOVE },
        {
          onSettled: () => {
            sendProductInteractionAction('ProductUnlike');
          },
          onError: () => {
            setIsLiked(true);
            setLikeCount((prev) => (prev !== undefined ? prev + 1 : prev));
          },
        }
      );
    }
  }

  return (
    <div className={styles.container}>
      <ButtonMinimal
        onClick={handleClick}
        title={(isLiked ? 'Unlike' : 'Like') + ' Product'}
        {...(!isAuthenticated && {
          'aria-haspopup': 'dialog',
          ...(isModalOpen && { 'aria-controls': 'product-card-like-modal' }),
        })}
        className={styles.likeButton}
      >
        <IconComponent />
      </ButtonMinimal>
      {hasLikes && <LikeCount count={likeCount} />}
      {!isAuthenticated && (
        <Modal
          id="product-card-like-modal"
          isOpen={isModalOpen}
          setOpen={setIsModalOpen}
          content={() => (
            <DownloadAppModal downloadAppTrackingId="product_card_like" />
          )}
          label="Get the App - Download Modal"
          overlayAriaLabel="Get the App - Download Modal"
        />
      )}
    </div>
  );
}

export function ProductCardLike({ guestLikeProduct, ...props }: Props) {
  const isGuestLikesEnabled = useIsGuestLikesEnabled();

  if (isGuestLikesEnabled && guestLikeProduct) {
    return (
      <GuestLikesProductCardLike
        product={guestLikeProduct}
        likeCount={props.likeCount}
      />
    );
  }

  return <DefaultProductCardLike {...props} />;
}
